import * as React from 'react'
import { useAccountsWithDuplicateSubscriptions, type AccountWithDuplicateSubscriptions } from './api/get-accounts-with-duplicate-subscriptions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/DataTable'
import { ColumnDef, type PaginationState } from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'
import moment from 'moment'

const columns: ColumnDef<AccountWithDuplicateSubscriptions>[] = [
    {
        accessorKey: 'name',
        header: 'Account',
        cell: ({ row }) => {
            const account = row.original
            return (
                <Link to={`/accounts/${account.accountId}`} className='text-primary hover:underline flex items-center gap-1'>
                    <Building2 className='h-4 w-4' />
                    {account.name}
                </Link>
            )
        },
    },
    {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <span>{row.getValue('email')}</span>,
    },
    {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ row }) => <span>{row.getValue('phone')}</span>,
    },
    {
        accessorKey: 'city',
        header: 'Location',
        cell: ({ row }) => {
            const city = row.getValue('city') as string | null
            const state = row.original.state
            return <span>{[city, state].filter(Boolean).join(', ') || '—'}</span>
        },
    },
    {
        accessorKey: 'accountScopeSubscriptionCount',
        header: 'Account Scope',
        cell: ({ row }) => {
            const count = row.getValue('accountScopeSubscriptionCount') as number
            return <Badge variant={count >= 2 ? 'destructive' : 'outline'}>{count}</Badge>
        },
    },
    {
        accessorKey: 'storeScopeSubscriptionCount',
        header: 'Store Scope',
        cell: ({ row }) => {
            const count = row.getValue('storeScopeSubscriptionCount') as number
            return <Badge variant={count > 0 ? 'outline' : 'secondary'}>{count}</Badge>
        },
    },
    {
        accessorKey: 'documentsUploadedInLast30Days',
        header: 'documents in last 30 days',
        cell: ({row}) => {
            const count = row.getValue('documentsUploadedInLast30Days') as number
            return <span>{count}</span>
        }
    },
    {
        accessorKey: 'createdDate',
        header: 'Created Date',
        cell: ({ row }) => {
            const date = row.getValue('createdDate')
            return <span>{date ? moment(date).format('MM/DD/YYYY') : ''}</span>
        },
    },
    {
        accessorKey: 'isDisabled',
        header: 'Status',
        cell: ({ row }) => {
            const isDisabled = row.getValue('isDisabled') as boolean
            return (
                <Badge variant={isDisabled ? 'destructive' : 'default'}>
                    {isDisabled ? 'Disabled' : 'Active'}
                </Badge>
            )
        },
    },
]

export default function AccountsWithDuplicateSubscriptionsReport() {
    const { data: accountsWithDuplicates = [], isLoading } = useAccountsWithDuplicateSubscriptions({})
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25,
    })

    // Manually paginate the data for client-side pagination
    const paginatedData = React.useMemo(() => {
        const start = pagination.pageIndex * pagination.pageSize
        const end = start + pagination.pageSize
        return accountsWithDuplicates.slice(start, end)
    }, [accountsWithDuplicates, pagination.pageIndex, pagination.pageSize])

    const handleExport = () => {
        if (!accountsWithDuplicates.length) return

        const csvRows = [
            ['Account Name', 'Email', 'Phone', 'City', 'State', 'Account Scope Count', 'Store Scope Count', 'Status'].join(','),
            ...accountsWithDuplicates.map(account => [
                `"${account.name.replace(/"/g, '""')}"`,
                `"${account.email.replace(/"/g, '""')}"`,
                `"${account.phone.replace(/"/g, '""')}"`,
                `"${(account.city || '').replace(/"/g, '""')}"`,
                `"${(account.state || '').replace(/"/g, '""')}"`,
                account.accountScopeSubscriptionCount,
                account.storeScopeSubscriptionCount,
                account.isDisabled ? 'Disabled' : 'Active',
            ].join(','))
        ]

        const csvContent = csvRows.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `accounts_with_duplicate_subscriptions_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle>Accounts With Duplicate Subscriptions</CardTitle>
                            <p className='text-sm text-muted-foreground mt-1'>
                                Accounts that have either 2+ account-scope subscriptions or both account-scope and store-scope subscriptions
                            </p>
                        </div>
                        <Button onClick={handleExport} variant='outline' disabled={isLoading || accountsWithDuplicates.length === 0}>
                            <Download className='h-4 w-4 mr-2' />
                            Export CSV
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className='text-sm text-muted-foreground'>Loading accounts...</div>
                    ) : (
                        <>
                            {accountsWithDuplicates.length === 0 ? (
                                <div className='text-sm text-muted-foreground'>No accounts with duplicate subscriptions found.</div>
                            ) : (
                                <>
                                    <div className='mb-4 text-sm text-muted-foreground'>
                                        Found {accountsWithDuplicates.length} account{accountsWithDuplicates.length !== 1 ? 's' : ''} with duplicate subscriptions
                                    </div>
                                    <DataTable
                                        columns={columns}
                                        data={paginatedData}
                                        isLoading={isLoading}
                                        enablePagination={true}
                                        serverPagination={true}
                                        pageIndex={pagination.pageIndex}
                                        pageSize={pagination.pageSize}
                                        pageCount={Math.ceil(accountsWithDuplicates.length / pagination.pageSize)}
                                        onPaginationChange={setPagination}
                                        serverSorting={false}
                                        scrollContainerClassName='max-h-[calc(100vh-400px)] overflow-y-auto'
                                    />
                                </>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

